import { KeyboardEvent, PointerEvent, useEffect, useRef, useState } from 'react';

export type ResizeAxis = 'column' | 'row';
export const MIN_COLUMN_WIDTH = 56;
export const MIN_ROW_HEIGHT = 30;
const KEYBOARD_STEP = 8;
type Sizes = Record<string, number>;

/** Freeze column widths before resizing; only rows explicitly resized by the user get a fixed height. */
export default function useTableSizing() {
  const tableRef = useRef<HTMLTableElement>(null);
  const initialColumnWidthsRef = useRef<Sizes>({});
  const [narrowedColumns, setNarrowedColumns] = useState<Record<string, boolean>>({});
  const cleanupRef = useRef<() => void>(() => {});
  const [columnWidths, setColumnWidths] = useState<Sizes>({});
  const [rowHeights, setRowHeights] = useState<Sizes>({});
  const [resizeAxis, setResizeAxis] = useState<ResizeAxis | null>(null);

  useEffect(() => () => cleanupRef.current(), []);

  function freezeLayout() {
    const widths: Sizes = {};
    const heights: Sizes = {};
    const table = tableRef.current;
    Array.from(table.tHead.rows[0].cells).forEach((cell) => {
      widths[cell.dataset.columnId] = cell.getBoundingClientRect().width;
    });
    Array.from(table.tBodies[0].rows).forEach((row) => {
      if (row.dataset.rowId) {
        heights[row.dataset.rowId] = row.getBoundingClientRect().height;
      }
    });
    if (!Object.keys(initialColumnWidthsRef.current).length) {
      initialColumnWidthsRef.current = widths;
    }
    setColumnWidths(widths);
    return { column: widths, row: heights };
  }

  function updateSize(axis: ResizeAxis, id: string, size: number) {
    const minimum = axis === 'column' ? MIN_COLUMN_WIDTH : MIN_ROW_HEIGHT;
    const setSizes = axis === 'column' ? setColumnWidths : setRowHeights;
    const nextSize = Math.max(minimum, size);
    setSizes((sizes) => ({ ...sizes, [id]: nextSize }));
    if (axis === 'column') {
      setNarrowedColumns((columns) => ({ ...columns, [id]: nextSize < initialColumnWidthsRef.current[id] }));
    }
  }

  function startResize(event: PointerEvent<HTMLElement>, axis: ResizeAxis, id: string) {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    cleanupRef.current();
    const startSize = freezeLayout()[axis][id];
    const startPosition = axis === 'column' ? event.clientX : event.clientY;
    const pointerId = event.pointerId;
    const handle = event.currentTarget;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    document.body.style.cursor = axis === 'column' ? 'col-resize' : 'row-resize';
    document.body.style.userSelect = 'none';
    setResizeAxis(axis);
    handle.setPointerCapture(pointerId);

    function move(moveEvent: globalThis.PointerEvent) {
      if (moveEvent.pointerId === pointerId) {
        const position = axis === 'column' ? moveEvent.clientX : moveEvent.clientY;
        updateSize(axis, id, startSize + position - startPosition);
      }
    }
    function finish(finishEvent: globalThis.PointerEvent) {
      if (finishEvent.pointerId === pointerId) {
        cleanupRef.current();
      }
    }
    function cleanup() {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', finish);
      window.removeEventListener('blur', cleanup);
      if (handle.hasPointerCapture(pointerId)) {
        handle.releasePointerCapture(pointerId);
      }
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      setResizeAxis(null);
      cleanupRef.current = () => {};
    }
    cleanupRef.current = cleanup;
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerup', finish);
    document.addEventListener('pointercancel', finish);
    window.addEventListener('blur', cleanup);
  }

  function resizeWithKeyboard(event: KeyboardEvent<HTMLElement>, axis: ResizeAxis, id: string) {
    const decrease = axis === 'column' ? 'ArrowLeft' : 'ArrowUp';
    const increase = axis === 'column' ? 'ArrowRight' : 'ArrowDown';
    if (event.key !== decrease && event.key !== increase) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const size = freezeLayout()[axis][id];
    updateSize(axis, id, size + (event.key === increase ? KEYBOARD_STEP : -KEYBOARD_STEP));
  }

  return { tableRef, columnWidths, narrowedColumns, rowHeights, resizeAxis, startResize, resizeWithKeyboard };
}
